// Dashboard layout state + persistence.
//
// Built-in views (operations / provider / executive) persist to localStorage,
// one key each, with per-view default layouts. CUSTOM dashboards (Part C) are
// server-backed: their items come from the server `config` and changes are
// pushed back via an `onSave` callback (debounced by the caller). Both share the
// same add/remove/resize/per-widget-config logic.
import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_LAYOUTS, WIDGET_BY_ID, widgetsForDashboard } from './widgets';

export interface GridItem {
  i: string; x: number; y: number; w: number; h: number;
  /** Per-widget user settings (Part B). Optional; shape is per the widget's
   *  configSchema. Persisted alongside geometry in the same layout blob. */
  config?: Record<string, unknown>;
}

/** Custom (server-backed) source for a user-created dashboard. */
export interface CustomSource {
  /** Stable id used to key the offered-widget catalog (custom = operations set). */
  catalog: string;
  /** Items loaded from the server (null while loading → empty grid). */
  initialItems: GridItem[];
  /** Persist the current items (debounced server PUT). */
  onSave: (items: GridItem[]) => void;
}

const keyFor = (dashboard: string) => `iosentra.dashboard.${dashboard}.v5`;
const defaultFor = (dashboard: string) => DEFAULT_LAYOUTS[dashboard] ?? DEFAULT_LAYOUTS.operations;

function load(dashboard: string): GridItem[] {
  const def = defaultFor(dashboard);
  try {
    const raw = localStorage.getItem(keyFor(dashboard));
    if (!raw) return def;
    const parsed = JSON.parse(raw) as GridItem[];
    const valid = parsed.filter((it) => WIDGET_BY_ID[it.i]);
    return valid.length ? valid : def;
  } catch {
    return def;
  }
}

export function useDashboardLayout(dashboard = 'operations', custom?: CustomSource) {
  const isCustom = !!custom;
  // The catalog of widgets this view offers: custom dashboards reuse the
  // 'operations' set (the full self-serve widget catalog).
  const catalog = isCustom ? (custom!.catalog || 'operations') : dashboard;

  const [items, setItems] = useState<GridItem[]>(() =>
    isCustom ? (custom!.initialItems ?? []) : load(dashboard));

  // Re-seed when the source identity changes (switching dashboards/views).
  const sourceKey = isCustom ? `custom:${catalog}:${JSON.stringify(custom!.initialItems?.map((i) => i.i))}` : dashboard;
  const lastKey = useRef(sourceKey);
  useEffect(() => {
    if (lastKey.current === sourceKey) return;
    lastKey.current = sourceKey;
    setItems(isCustom ? (custom!.initialItems ?? []) : load(dashboard));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey]);

  // Persist on change: localStorage for built-ins, server callback for custom.
  const onSaveRef = useRef(custom?.onSave);
  onSaveRef.current = custom?.onSave;
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; } // don't save the initial load
    if (isCustom) { onSaveRef.current?.(items); return; }
    try { localStorage.setItem(keyFor(dashboard), JSON.stringify(items)); } catch { /* ignore quota */ }
  }, [items, dashboard, isCustom]);

  const setLayout = useCallback((next: GridItem[]) => {
    setItems((prev) => next.map((n) => ({ ...prev.find((p) => p.i === n.i), i: n.i, x: n.x, y: n.y, w: n.w, h: n.h })));
  }, []);

  const addWidget = useCallback((id: string) => {
    setItems((prev) => {
      if (prev.some((p) => p.i === id)) return prev;
      const def = WIDGET_BY_ID[id];
      const maxY = prev.reduce((m, p) => Math.max(m, p.y + p.h), 0);
      return [...prev, { i: id, x: 0, y: maxY, w: def.defaultSize.w, h: def.defaultSize.h }];
    });
  }, []);

  const removeWidget = useCallback((id: string) => setItems((prev) => prev.filter((p) => p.i !== id)), []);

  const setWidgetConfig = useCallback((id: string, patch: Record<string, unknown>) => {
    setItems((prev) => prev.map((p) => (p.i === id ? { ...p, config: { ...(p.config ?? {}), ...patch } } : p)));
  }, []);

  // Reset: built-ins go to their default; a custom dashboard clears to empty.
  const reset = useCallback(() => setItems(isCustom ? [] : defaultFor(dashboard)), [dashboard, isCustom]);

  const available = widgetsForDashboard(catalog).filter((w) => !items.some((it) => it.i === w.id));

  return { items, setLayout, addWidget, removeWidget, setWidgetConfig, reset, available };
}
